import { ChipEnterprise, ChipPro, ChipStartup } from '@capitalise/ui-design-system';

export enum CountryCode {
  gb = 'GB',
  za = 'ZA',
}
export type TCountryCode = `${CountryCode}`;

export type CurrencyCode = 'GBP' | 'ZAR';
export type UserType = 'business' | 'introducer';
export interface Timezone {
  tzDatabaseName: string;
  utcOffset: number;
}

export interface Locale {
  countryCode: CountryCode;
  currencyCode: CurrencyCode;
  timezone: Timezone;
}

export enum UserCompanyType {
  soleTrader = 'SOLE_TRADER',
  limitedCompany = 'LIMITED_COMPANY',
  llp = 'LLP',
  lpp = 'LPP',
  limitedLiabilityPartnership = 'LIMITED_LIABILITY_PARTNERSHIP',
}

export interface UserCompany {
  company_id: number;
  name: string;
  type: UserCompanyType;
}

export interface Level {
  id: number;
  name: MembershipLevels;
  key: string;
  priority: number;
}

export interface DefaultLevel extends Omit<Level, 'key'> {
  features: {
    name: string;
    value: boolean | number | string;
    rawValue: string;
    rawValueType: 'boolean' | 'integer' | 'string';
  }[];
  isEligible: boolean;
  requirements: unknown[];
}

export interface Membership {
  defaultLevel: DefaultLevel;
  featureEligibity: {
    features: unknown[];
    levels: unknown[];
  };
  features: MembershipFeatureFlags;
  level: Level;
  highestLevel: DefaultLevel;
  lowestLevel: DefaultLevel;
}

export interface User {
  company: UserCompany;
  created: number;
  email: string;
  fundingSearchCount?: number;
  hasFundingSearch?: boolean;
  hasNetworkManagerRole?: boolean;
  id: number;
  locale: Locale;
  membership?: Membership;
  name: string;
  userHash: string;
  userType: UserType;
}

export const MembershipLevelsChips = Object.freeze({
  'Business-Enterprise': ChipEnterprise,
  'Business-Startup': ChipStartup,
  'Business-Pro': ChipPro,
});

export enum MembershipLevels {
  free = 'Business-Free',
  freeZA = 'Business-Free-ZA',
  enterprise = 'Business-Enterprise',
  startup = 'Business-Startup',
  pro = 'Business-Pro',
}

export enum MembershipFeatureFlagKeys {
  productToday = 'product_today',
  productCreditProfile = 'product_credit_profile',
  featureCreditScore = 'feature_credit_score',
  featureCreditHistory = 'feature_credit_history',
  featureRiskFactors = 'feature_risk_factors',
  featurePaymentPerformance = 'feature_payment_performance',
  featureCashCapital = 'feature_cash_capital',
  featureLegalNotices = 'feature_legal_notices',
  productMonitor = 'product_monitor',
  productMonitorLimit = 'product_monitor_limit',
  productApplications = 'product_applications',
  productCreditReview = 'product_credit_review',
  productCreditReviewPrice = 'product_credit_review_price',
  featureCreditReportDownload = 'feature_credit_report_download',
  featurePublicRecord = 'feature_public_record',
  featureCreditSearches = 'feature_credit_searches',
}

export type UpgradeType = 'upgrade' | 'actual' | false;
export type RiskFactorType = 'unverified' | 'all' | false | true;

export interface MembershipFeatureFlags {
  [MembershipFeatureFlagKeys.productToday]: boolean;
  [MembershipFeatureFlagKeys.productCreditProfile]: boolean;
  [MembershipFeatureFlagKeys.featureCreditScore]: UpgradeType;
  [MembershipFeatureFlagKeys.featureCreditHistory]: boolean;
  [MembershipFeatureFlagKeys.featureRiskFactors]: RiskFactorType;
  [MembershipFeatureFlagKeys.featurePaymentPerformance]: boolean;
  [MembershipFeatureFlagKeys.featureCashCapital]: boolean;
  [MembershipFeatureFlagKeys.featureLegalNotices]: boolean;
  [MembershipFeatureFlagKeys.productMonitor]: boolean;
  [MembershipFeatureFlagKeys.productMonitorLimit]: number;
  [MembershipFeatureFlagKeys.productApplications]: boolean;
  [MembershipFeatureFlagKeys.productCreditReview]: boolean;
  [MembershipFeatureFlagKeys.productCreditReviewPrice]: string;
  [MembershipFeatureFlagKeys.featureCreditReportDownload]: boolean;
  [MembershipFeatureFlagKeys.featurePublicRecord]: boolean;
  [MembershipFeatureFlagKeys.featureCreditSearches]: string;
}

export interface AuthContext {
  logout: () => void;
  user?: User;
  apiKey: string;
  demasquerade?: () => void;
  loading: boolean;
  isPro: boolean;
  limitRelatedCompanies: number;
}

export type ScoreGrade = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H';

export enum CaseType {
  funding = 'FUNDING',
  recoveryAndProtect = 'RECOVERY_AND_PROTECT', //R&D Tax Claim - Debt recovery
  raise = 'RAISE', // Innovation Grant
  creditImprovement = 'PROTECT', // Credit Improvement
  innovationGrant = 'GRANT', // Innovation Grant
}

export enum ProductType {
  termLoan = 'TERM_LOAN',
  fixedLoanTerm = 'FIXED_LOAN_TERM',
  assetBackedFinance = 'ASSET_BACKED_FINANCE',
  creditReview = 'CREDIT_REVIEW',
  merchantCashAdvance = 'MERCHANT_CASH_ADVANCE',
  invoiceFinancing = 'INVOICE_FINANCE',
  ecommerceFunding = 'ECOMMERCE_FUNDING',
  tradeFinance = 'TRADE_FINANCE',
  commercialProperty = 'COMMERCIAL_PROPERTY',
  debtRecovery = 'DEBT_RECOVERY',
  researchAndDevelopmentTax = 'RESEARCH_AND_DEVELOPMENT_TAX',
  capitalAllowances = 'CAPITAL_ALLOWANCES',
  creditImprovement = 'CREDIT_IMPROVEMENT',
  grants = 'GRANTS',
  governmentBacked = 'GOVERNMENT_BACKED',
  recoveryLoanScheme = 'RECOVERY_LOAN_SCHEME',
  creditCard = 'CREDIT_CARD',
}

export interface IPreview {
  creditScoreGrade: string;
  creditScore: string;
  creditLimit: string;
  sector: number;
  creditScoreText: string;
}

export interface ICompanyInfo {
  name: string;
  registrationNumber: string;
}

export interface IRatios {
  quick: string;
}

export interface IBalanceSheet {
  createDate: Date;
  chartName: string;
  revenue: number;
  profitNet: number;
  netAssets: number;
  fixedAssets: number;
  ratios: IRatios;
}

export interface IDirector {
  title: string;
  name: string;
  address: string;
  appointmentDate: Date;
  nationality: string;
}

export interface ICompanyDetails {
  companyName: string;
  registrationRef: string;
  companyType: string;
  dissolved: boolean;
  countryCode: string;
  normalizedSector: string;
  normalizedSectorId: number;
  activitySectorCode: string;
  activitySectorDescription: string;
  directors: IDirector[];
  registrationDate: Date;
  contactPhone: string;
  companyWebsite: string;
  location?: ILocation;
}

export interface ICcjDetail {
  judgmentType: string;
  judgmentSource: string;
  judgmentDate: string;
  judgmentAmount: number;
  judgmentAgainst: string;
  courtName: string;
  caseNumber: string;
}

export interface ICompany {
  ccjLast12MonthsCount: number;
  ccjLast12MonthsValue: number;
  ccjDetails: ICcjDetail[];
}

export interface ILocation {
  county: string;
  district: string;
  houseNumber: string;
  postCode: string;
  street: string;
  town: string;
}

export interface ICcjsDirector {
  ccjLast12MonthsCount: number;
  ccjLast12MonthsValue: number;
}

export interface ICcjs {
  company: ICompany;
  directors: ICcjsDirector[];
}

export interface ICreditEnquiries {
  last3Months: number;
  last6Months: number;
  last12Months: number;
  lastEnquiryDate: Date;
}

export interface ICreditScore {
  scoreAvgSector: string;
  scoreAvgAge: string;
  scoreMarginAll: string;
  scoreMarginSector: string;
  scoreMarginAge: string;
}

export interface ICreditScoreHistoryData {
  historyCreditLimit: number;
  historyCreditScore: number;
  historyDate: Date;
}
export interface ICreditScoreHistory {
  avgScore12Months: number;
  avgScoreBand12Months: string;
  avgScoreBand12Text: string;
  scoreHistory: ICreditScoreHistoryData[];
}

export interface IProbabilityOfDefault {
  defaultAvgAll: string;
  defaultAvgSector: string;
  defaultAvgAge: string;
}

export interface IBenchmark {
  creditScore: ICreditScore;
  probabilityOfDefault: IProbabilityOfDefault;
}

export interface IPaymentPerformanceCompany {
  paymentPattern: string;
  avgDaysBeyondTerms12Months: number;
}

export interface IPaymentPerformanceIndustry {
  industryAvgDaysBeyondTerms12Months: number;
}

export interface IPaymentPerformance {
  company: IPaymentPerformanceCompany;
  industry: IPaymentPerformanceIndustry;
}

export enum CreditReportProviders {
  experian = 'Experian',
  creditSafe = 'CreditSafe',
}

export interface IFillingHistory {
  accountsFilingDate: string;
  accountsLastFiledDate: string;
  accountsRefDate: { day: string; month: string };
  returnsFilingDate: string;
  returnsLastFiledDate: string;
  latestRatingChangeDate: string;
}

export interface ICreditReportExperian {
  provider: CreditReportProviders.experian;
  creditScore: string;
  creditScoreGrade: string;
  creditScoreBand: string;
  creditScoreText: string;
  creditScoreOverride: boolean;
  creditScoreHistory?: ICreditScoreHistory;
  creditLimit: string;
  creditLimitPrevious: string;
  creditLimitOverride: boolean;
  probabilityOfDefault: number;
  creditScorePrevious: string;
  creditRating: string;
  companyDetails: ICompanyDetails;
  ccjs: ICcjs;
  creditEnquiries: ICreditEnquiries;
  creditRatePrevious: string;
  benchmark: IBenchmark;
  paymentPerformance: IPaymentPerformance;
  windUp?: unknown;
  filingHistory: IFillingHistory;
  riskFactorsSummary: {
    categories: [object];
  };
}

export interface INarrativeVar {
  name: string;
  value: string;
  type: string;
}

export interface ICreditReport {
  name: string;
  rule: string;
  purpose: number;
  rank: string;
  type: string;
  category: string;
  description: string;
  narrativeHeadline: string;
  narrative: string;
  narrativeVars: INarrativeVar[];
  hidden: boolean;
  recommendedProductsNames: unknown[];
  recommendedProducts: unknown[];
}

export interface IInsight {
  creditReports: ICreditReport[];
}

export type ICreditReportCreditSafe = Record<string, unknown> & {
  provider: CreditReportProviders.creditSafe;
};

export interface ISnapshot {
  entityId: string;
  generatedAt: Date;
  balanceSheet: IBalanceSheet;
  companyInfo: ICompanyInfo;
  creditReports: (ICreditReportCreditSafe | ICreditReportExperian)[];
  insights?: IInsight;
  preview: IPreview;
  companyRelation: string;
  startDate: Date;
  endDate: Date;
}

export enum FacilitySource {
  publicAccountingProvider = 'PUBLIC_ACCOUNTING_PROVIDER',
  privateAccountingProvider = 'PRIVATE_ACCOUNTING_PROVIDER',
}

export enum FacilityStatus {
  positive = 'LOOKING_GOOD',
  negative = 'NEEDS_ATTENTION',
  neutral = 'NEUTRAL',
}

export interface IFacility {
  source: FacilitySource;
  impact: FacilityStatus;
  id: number;
  type: string;
  createdAt?: string;
  accountsHistory?: any[];
}

export interface IAddress {
  houseNumber: string;
  street: string;
  town: string;
  county: string;
  district: string;
  postCode: string;
}
